import type { CSSProperties, ReactNode } from 'react';

import { languageDirection, type PublicLanguage, type PublicTenant } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import { brandingStyle } from '../branding';
import { copyFor } from '../copy';
import { BrowserChrome } from './BrowserChrome';

export interface PublicReportAdminFrame {
    /** De donkere kop van de kantoorpreview. */
    header: ReactNode;
    /** De rechterkolom: telefoonvoorbeeld, linkstatistieken en toegang. */
    sidebar: ReactNode;
    /** De link zoals hij in de adresbalk van de klant staat. */
    chromeUrl: string | null;
}

export interface PublicReportLayoutProps {
    tenant: PublicTenant | null;
    language: PublicLanguage;
    /** Zonder `admin` is dit wat de koper ziet; met `admin` de kantoorpreview. */
    admin?: PublicReportAdminFrame;
    /**
     * De pagina vult het vlak waarin ze staat in plaats van het venster: het bord
     * scrolt dan zelf en de pagina mag breder worden. Zo bekijkt kantoor het
     * rapport over het hele scherm, zonder chroom en zonder kolom ernaast.
     */
    filling?: boolean;
    children: ReactNode;
}

/**
 * Klantpagina (scherm 12): het bord met de witte pagina in de huisstijl van het
 * keuringsbedrijf.
 *
 * Wat het bedrijf ziet is niet wat de klant ziet: de donkere kop, het
 * browserchroom en de hele rechterkolom bestaan alleen in de admin-modus. De
 * pagina zelf - rapport, codeveld of foutmelding - is in beide modi hetzelfde
 * stuk markup.
 */
export function PublicReportLayout({
    tenant,
    language,
    admin,
    filling = false,
    children,
}: PublicReportLayoutProps) {
    const rootStyle: CSSProperties = {
        ...brandingStyle(tenant),
        containerType: 'inline-size',
    };

    const pageStyle = {
        containerType: 'inline-size',
        '--pr-pad-x': 'clamp(16px, 4cqi, 30px)',
    } as CSSProperties;

    return (
        <div style={rootStyle} className="flex min-h-full flex-col bg-[var(--surface-board)]">
            {admin ? admin.header : null}

            {/*
             * In de kantoorpreview en in de volledige weergave scrolt dit vlak binnen de
             * app. Op de publieke pagina scrolt het venster: dan is dit vlak geen
             * scrollcontainer, anders blijven de tabbladen niet bovenaan staan tijdens
             * het lezen.
             *
             * De ruimte rond de pagina zit op het vlak daarbinnen, niet op de scroller:
             * Chrome en Safari laten een plakkende balk binnen de padding van zijn
             * scroller stoppen. Met de padding hierop bleven de tabbladen 22 px onder de
             * donkere kop hangen, en in die strook schoof de tekst zichtbaar langs.
             */}
            <div className={cn('flex-1', admin || filling ? 'overflow-auto' : 'overflow-x-clip')}>
                <div className="flex flex-wrap items-start justify-center gap-[18px] px-[clamp(8px,3cqi,24px)] pt-[22px] pb-10">
                    <article
                        style={pageStyle}
                        lang={language}
                        dir={languageDirection(language)}
                        className={cn(
                            'w-full flex-[1_1_620px] overflow-clip rounded-[12px] border border-[var(--placeholder-border)] bg-[var(--surface-card)] shadow-[var(--shadow-public-page)]',
                            filling
                                ? 'max-w-[var(--public-page-max-wide)]'
                                : 'max-w-[var(--public-page-max)]',
                        )}
                    >
                        {admin?.chromeUrl ? (
                            <BrowserChrome
                                url={admin.chromeUrl}
                                secureLabel={copyFor(language).secureLabel}
                            />
                        ) : null}

                        {children}
                    </article>

                    {admin ? admin.sidebar : null}
                </div>
            </div>
        </div>
    );
}
